// Bootstrap: monta el shell, carga datos y mapa, y cablea pestañas, fichas y tiempo real. Nada
// de lógica de negocio aquí: cada pieza vive en su módulo.

import "./styles.css";
import { ELCHE_CENTER, ELCHE_ZOOM, HIDDEN_GROUPS } from "./config";
import { loadDataset, stopHeadings, type LineId } from "./data";
import type { LatLng } from "./geo";
import { loadMap, onGoogleAuthFailure, setGoogleKey, getGoogleKey } from "./map/loader";
import { mountShell, showToast } from "./ui/shell";
import { loadLinesState, mountLinesPanel } from "./ui/linesPanel";
import { mountRoutePanel, type RoutePanelHandle } from "./ui/routePanel";
import { mountNearPanel } from "./ui/nearPanel";
import { createDetailHost } from "./ui/detail";
import { stopView } from "./ui/stopView";
import { lineView } from "./ui/lineView";
import { createGps } from "./ui/gps";
import { parseLinesParam, parsePointParam } from "./urlparams";
import { resolveAddress as resolveGoogle } from "./map/googlePlaces";
import { resolveAddress as resolvePhoton } from "./map/photon";
import { openAbout } from "./ui/about";
import { enableSheetDrag } from "./ui/sheetDrag";
import { createLiveTracker } from "./live/tracker";

type Tab = "near" | "lines" | "route";

// Versión nueva publicada: el service worker nuevo se activa solo (skipWaiting) y aquí
// recargamos para que se vea ya, sin tener que cerrar la app. Al volver a la app (p. ej. desde
// segundo plano en el móvil) se comprueba si hay versión nueva.
function watchUpdates(): void {
  if (!("serviceWorker" in navigator)) return;
  const hadController = navigator.serviceWorker.controller !== null;
  let reloading = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    // La primera instalación también cambia de controlador: ahí no hace falta recargar.
    if (!hadController || reloading) return;
    reloading = true;
    window.location.reload();
  });
  const check = (): void => {
    void navigator.serviceWorker.getRegistration().then((r) => r?.update()).catch(() => {});
  };
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) check();
  });
  window.setInterval(check, 30 * 60_000);
}

async function main(): Promise<void> {
  watchUpdates();
  const root = document.getElementById("app");
  if (!root) throw new Error("No hay #app en el DOM");
  const shell = mountShell(root);
  const toast = (t: string): void => showToast(shell.msg, t);

  onGoogleAuthFailure(() => {
    toast("Google rechazó la clave. Vuelvo a OpenStreetMap.");
    setGoogleKey("");
    window.setTimeout(() => window.location.reload(), 1500);
  });

  const [ds, mapResult] = await Promise.all([loadDataset("/data", HIDDEN_GROUPS), loadMap()]);
  const { adapter, provider } = mapResult;
  await adapter.init(shell.map, { center: ELCHE_CENTER, zoom: ELCHE_ZOOM });

  adapter.setLines(ds.lines.map((l) => ({ id: l.id, color: l.color, paths: l.paths })));
  const headings = stopHeadings(ds);
  adapter.setStops(
    ds.stops.filter((s) => s.lines.length > 0).map((s) => ({
      idx: s.idx,
      number: s.number,
      lines: s.lines.slice(),
      ll: s.ll,
      heading: headings.get(s.idx) ?? null,
    })),
  );
  adapter.setArrows(ds.arrows.map((a) => ({ line: a.line, ll: a.ll, bearing: a.bearing })));

  // Alto que tapa el panel, para centrar el mapa en la parte visible.
  const bottomPad = (): number =>
    shell.sheet.classList.contains("min") ? 90 : shell.sheet.getBoundingClientRect().height;
  const expand = (): void => shell.sheet.classList.remove("min");

  const tracker = createLiveTracker({
    ds,
    adapter,
    onError: () => toast("Tiempo real no disponible ahora. Lo reintento solo."),
  });
  tracker.setEnabled(true);

  const gps = createGps({
    adapter,
    toast,
    onFollowChange: (following) => shell.loc.classList.toggle("on", following),
  });

  // --- Pestañas y fichas ----------------------------------------------------------------

  let tab: Tab = "near";
  const panels: Record<Tab, HTMLElement> = { near: shell.pNear, lines: shell.pLines, route: shell.pRoute };
  const tabBtns: Record<Tab, HTMLButtonElement> = {
    near: shell.tabNear,
    lines: shell.tabLines,
    route: shell.tabRoute,
  };

  const detail = createDetailHost(shell.pDetail, (open) => {
    for (const t of Object.keys(panels) as Tab[]) panels[t].hidden = open || t !== tab;
    near.setActive(!open && tab === "near");
  });

  const query = window.location.search.replace(/^\?/, "");
  const initial = loadLinesState();
  const fromUrl = parseLinesParam(query);
  if (fromUrl) initial.on = fromUrl;
  // Líneas guardadas o del enlace que ya no se enseñan (grupos ocultos).
  initial.on = initial.on.filter((id) => ds.linesById.has(id));

  const lines = mountLinesPanel({
    ds,
    adapter,
    el: shell.pLines,
    initial,
    onChange: (on) => tracker.setLines(on),
    onOpenLine: (l) => openLine(l),
  });

  const openStop = (idx: number): void => {
    expand();
    detail.push(
      stopView(
        {
          ds,
          adapter,
          bottomPad,
          onRoute: (i, role) => {
            routePanel.setEndpointFromStop(i, role);
            switchTab("route");
          },
          onOpenLine: (l) => openLine(l),
          atStop: (stopId, l) => tracker.atStop(stopId, l),
        },
        idx,
      ),
    );
  };
  const openLine = (l: LineId): void => {
    expand();
    detail.push(
      lineView(
        {
          ds,
          adapter,
          tracker,
          bottomPad,
          showOnly: (only) => lines.setTemporary(only ? [only] : null),
          onOpenStop: openStop,
        },
        l,
      ),
    );
  };

  const near = mountNearPanel({
    ds,
    el: shell.pNear,
    where: () => {
      const g = gps.lastPos();
      return g ? { ll: g, gps: true } : { ll: adapter.getCenter(), gps: false };
    },
    askGps: () => {
      gps.start(() => near.refresh());
    },
    onOpenStop: openStop,
  });

  const key = getGoogleKey();
  // El fondo de Google no implica buscar con Google: Places se activa y se factura aparte. Solo
  // con VITE_GOOGLE_PLACES=1 y, si no encuentra nada o falla, Photon.
  const googlePlaces =
    provider === "google" && !!key && import.meta.env.VITE_GOOGLE_PLACES === "1";
  const onEnterAddress = async (
    _which: "o" | "d",
    q: string,
  ): Promise<{ ll: LatLng; label: string } | null> => {
    if (googlePlaces) {
      const found = await resolveGoogle(key, q).catch(() => null);
      if (found) return found;
    }
    return resolvePhoton(q);
  };

  const routePanel: RoutePanelHandle = mountRoutePanel({
    ds,
    adapter,
    oTxt: shell.oTxt,
    dTxt: shell.dTxt,
    oGps: shell.oGps,
    oPick: shell.oPick,
    dPick: shell.dPick,
    res: shell.res,
    toast,
    requestGps: (cb) => {
      gps.start((ll) => cb(ll));
    },
    onEnterAddress,
    onArmPick: (armed) => {
      if (armed) shell.sheet.classList.add("min");
    },
    onLinesUsed: (used) => lines.setTemporary(used),
  });

  const switchTab = (t: Tab): void => {
    detail.clear();
    expand();
    if (tab === "route" && t !== "route") {
      adapter.drawRoute(null);
      lines.setTemporary(null);
    }
    tab = t;
    for (const k of Object.keys(panels) as Tab[]) {
      panels[k].hidden = k !== t;
      tabBtns[k].classList.toggle("act", k === t);
    }
    near.setActive(t === "near");
    if (t === "route") routePanel.plan();
  };
  shell.tabNear.addEventListener("click", () => switchTab("near"));
  shell.tabLines.addEventListener("click", () => switchTab("lines"));
  shell.tabRoute.addEventListener("click", () => switchTab("route"));

  // --- Mapa ------------------------------------------------------------------------------

  adapter.onStopClick(openStop);
  adapter.onBusClick((id) => {
    const text = tracker.describe(id);
    if (text) toast(text);
  });
  adapter.onClick(() => {
    if (routePanel.isArmed()) return;
    shell.sheet.classList.add("min");
  });
  shell.grip.addEventListener("click", () => shell.sheet.classList.toggle("min"));
  enableSheetDrag(shell.sheet, shell.sheet.querySelector<HTMLElement>("#body")!);

  shell.loc.addEventListener("click", () => {
    if (gps.isFollowing()) {
      gps.setFollow(false);
      return;
    }
    if (!gps.start(() => tab === "near" && !detail.isOpen() && near.refresh())) return;
    gps.setFollow(true);
  });

  // --- Ajustes ---------------------------------------------------------------------------

  shell.cfg.addEventListener("click", () => {
    shell.gkey.value = getGoogleKey();
    shell.dlg.classList.add("show");
  });
  shell.gcancel.addEventListener("click", () => shell.dlg.classList.remove("show"));
  shell.gok.addEventListener("click", () => {
    setGoogleKey(shell.gkey.value);
    window.location.reload();
  });
  shell.gdel.addEventListener("click", () => {
    setGoogleKey("");
    window.location.reload();
  });
  shell.gabout.addEventListener("click", () => {
    shell.dlg.classList.remove("show");
    openAbout();
  });

  // --- Arranque --------------------------------------------------------------------------

  const from = parsePointParam(query, "from");
  const to = parsePointParam(query, "to");
  if (from || to) {
    if (from) routePanel.setOrigin(from);
    if (to) routePanel.setDestination(to);
    switchTab("route");
  } else {
    switchTab("near");
    void locateOnOpen();
  }

  // Al abrir, centra el mapa en ti una vez (sin seguirte: eso es el botón) y «Cerca» pasa a
  // tus paradas. Si el permiso está denegado no insiste ni avisa.
  async function locateOnOpen(): Promise<void> {
    if (!("geolocation" in navigator) || !window.isSecureContext) return;
    try {
      const perm = await navigator.permissions?.query({ name: "geolocation" });
      if (perm?.state === "denied") return;
    } catch {
      // Sin API de permisos (algunos navegadores): se pide igual.
    }
    gps.start((ll) => {
      if (detail.isOpen() || tab !== "near") return;
      adapter.flyTo(ll, 16, bottomPad());
      near.refresh();
    });
  }
}

void main().catch((err: unknown) => {
  const app = document.getElementById("app") ?? document.body;
  const box = document.createElement("div");
  box.style.padding = "20px";
  box.style.fontFamily = "system-ui,sans-serif";
  box.textContent = `Bus Elx no ha podido arrancar: ${err instanceof Error ? err.message : String(err)}`;
  app.appendChild(box);
  console.error(err);
});
